import type { Me } from "../../../apps/console/src/api/me/types";

import type { RequestOptions } from "./api";
import { authenticatedRequest } from "./api";
import type { JWTToken } from "./authentication";
import { authenticate, wipeToken } from "./authentication";
import type { ClientConfiguration } from "./config";
import { getConfig, setConfig } from "./config";
import { entities } from "./model";
import type { EnvironmentAccessRole } from "./model/EnvironmentAccess";
import type {
  CreateAccessParams,
  DeleteAccessParams,
  UpdateAccessParams
} from "./model/EnvironmentType";
import type { InvitationEnvironmentType } from "./model/Invitation";
import type { APIObject } from "./model/Ressource";
import type { TicketQueryParams } from "./model/Ticket";

export type { RequestOptions } from "./api";
export type { JWTToken } from "./authentication";
export type { ClientConfiguration } from "./config";
export * from "./model";

export const request = authenticatedRequest;

export default class Client {
  authenticationPromise: Promise<JWTToken>;
  getAccountInfoPromise: Promise<Me> | undefined;

  constructor(authenticationConfig: ClientConfiguration) {
    setConfig(authenticationConfig);

    this.authenticationPromise = authenticate(authenticationConfig);
  }

  async getAccessToken() {
    return this.authenticationPromise;
  }

  getConfig() {
    return getConfig();
  }

  wipeToken() {
    wipeToken();
  }

  async reAuthenticate() {
    this.authenticationPromise = authenticate(getConfig(), true);

    return this.authenticationPromise;
  }

  /**
   * Get a single project by its ID.
   */
  async getProject(id: string) {
    return entities.Project.get({ id });
  }

  /**
   * Get the environments of project projectId
   *
   * @param string projectId
   *
   * @return Promise Environment[]
   */
  async getEnvironments(projectId: string) {
    return entities.Environment.query({ projectId });
  }

  /**
   * Get the environment environmentId of the projectId project
   *
   * @param string projectId
   * @param string environmentId
   *
   * @return Promise Environment
   */
  async getEnvironment(projectId: string, environmentId: string) {
    return entities.Environment.get({ projectId, id: environmentId });
  }

  /**
   * Get a list of variables.
   *
   * @param string projectId
   * @param int limit
   *
   * @return ProjectLevelVariable[]
   */
  async getProjectVariables(projectId: string, limit?: number) {
    return entities.ProjectLevelVariable.query({ projectId, limit });
  }

  /**
   * Get backups of environment {environmentId} of project {projectId}
   *
   * @param projectId
   * @param environmentId
   *
   * @return Promise EnvironmentBackup[]
   */
  async getEnvironmentBackups(projectId: string, environmentId: string) {
    return entities.EnvironmentBackup.query({ projectId, environmentId });
  }

  /**
   * Filter a request array to remove null values.
   *
   * @param object request
   *
   * @return object
   */
  cleanRequest(req: Record<string, any>) {
    const cleanedReq: Record<string, any> = {};
    const keys = Object.keys(req).filter(
      key => req[key] !== null && typeof req[key] !== "undefined"
    );

    for (const key of keys) {
      cleanedReq[key] = req[key];
    }

    return cleanedReq;
  }

  /**
   * Create a new Platform.sh subscription.
   *
   * @param string region  The region. See Subscription::$availableRegions.
   * @param string plan    The plan. See Subscription::$availablePlans.
   * @param string title   The project title.
   * @param string defaultBranch The default branch.
   * @param int    storage The storage of each environment, in MiB.
   * @param int    environments The number of available environments.
   * @param array  activationCallback An activation callback for the subscription.
   *
   * @return Subscription
   */
  async createOrganizationSubscription(config: APIObject) {
    const {
      organizationId,
      region,
      plan = "development",
      title,
      defaultBranch,
      storage,
      environments,
      optionsUrl,
      vendor,
      optionsCustom
    } = config;
    const values = this.cleanRequest({
      project_region: region,
      plan,
      project_title: title,
      default_branch: defaultBranch,
      storage,
      environments,
      options_url: optionsUrl,
      vendor,
      options_custom: optionsCustom,
      organizationId
    });

    return new entities.OrganizationSubscription(values).save();
  }

  /**
   * Get a subscription by its ID.
   *
   * @param string|int id
   *
   * @return Subscription|false
   */
  async getSubscription(id: string) {
    return entities.Subscription.get({ id });
  }

  /**
   * Get subscriptions.
   *
   * @param array filters
   *
   * @return Subscriptions[]
   */
  async getSubscriptions(filter: object, all?: boolean) {
    return entities.Subscription.query({ filter, all: all && 1 });
  }

  /**
   * Get organization subscriptions.
   *
   * @param string organizationId
   * @param array filters
   *
   * @return OrganizationSubscriptions[]
   */
  async getOrganizationSubscriptions(
    organizationId: string,
    params?: Record<string, any>
  ) {
    return entities.OrganizationSubscription.query({
      ...params,
      organizationId
    });
  }

  /**
   * Get organization subscription.
   *
   * @param string organizationId
   * @param string subscriptionId
   *
   * @return OrganizationSubscription
   */
  async getOrganizationSubscription(organizationId: string, id: string) {
    return entities.OrganizationSubscription.get({ organizationId, id });
  }

  /**
   * Get organization subscription can create, which determines if a
   * new subscription can be created under an organization.
   *
   * @param string organizationId
   */
  async getOrganizationSubscriptionCanCreate(organizationId: string) {
    return entities.OrganizationSubscription.getCanCreate({ organizationId });
  }

  /**
   * Initalize the environment for a new subscription
   *
   * @param string projectId
   * @param object options
   * @param string environmentId
   */
  async initializeEnvironment(
    projectId: string,
    options: RequestOptions,
    environmentId = "master"
  ) {
    const { api_url } = getConfig();

    return request(
      `${api_url}/projects/${projectId}/environments/${environmentId}/initialize`,
      "POST",
      options
    );
  }

  /**
   * Estimate the cost of a subscription for an organization.
   *
   * @param string plan         The plan (see Subscription::$availablePlans).
   * @param int    storage      The allowed storage per environment (in MiB).
   * @param int    environments The number of environments.
   * @param int    users        The number of users.
   * @param string backups      The available backup sellables
   *
   * @return array An array containing at least 'total' (a formatted price).
   */
  async getOrganizationSubscriptionEstimate(
    organizationId: string,
    params: {
      organizationId: string;
      plan: string;
      storage: number;
      environments: number;
      user_licenses: number;
      big_dev?: string;
      backups?: string;
      format?: string;
      country_code?: string;
    }
  ) {
    const { api_url } = getConfig();

    return request(
      `${api_url}/organizations/${organizationId}/subscriptions/estimate`,
      "GET",
      params
    );
  }

  /**
   * Get organization
   *
   * @param string id
   *
   * @return Organization
   */
  async getOrganization(id: string) {
    return entities.Organization.get({ id });
  }

  /**
   * Create organization
   *
   * @param object organization
   *
   * @return Organization
   */
  async createOrganization(organization: APIObject) {
    const newOrganization = new entities.Organization(organization);

    return newOrganization.save();
  }

  /**
   * Get organization orders
   *
   *
   * @return Account
   */
  async getOrganizationOrders(organizationId: string, filter: object) {
    return entities.OrganizationOrder.query({
      organizationId,
      filter
    });
  }

  /**
   * Get organization order
   */
  async getOrganizationOrder(
    organizationId: string,
    id: string,
    includeDetails?: boolean,
    asof?: string
  ) {
    return entities.OrganizationOrder.get({
      organizationId,
      id,
      includeDetails,
      asof
    });
  }

  /**
   * Get organization payment source.
   *
   * @param string organization id.
   *
   * @return Promise
   */
  async getOrganizationPaymentSource(
    organizationId: string,
    include_nonchargeable?: 1 | 0
  ) {
    return entities.OrganizationPaymentSource.get({
      organizationId,
      include_nonchargeable: include_nonchargeable ?? 0
    });
  }

  /**
   * Add a new payement source to an organization.
   *
   * @param string type The type of the payment source: credit-card /
   * stripe_sepa_debit.
   * @param string token The token returns by Stripe.
   * @param string email The email linked to the payment source.
   *
   * @return Result
   */
  async addOrganizationPaymentSource(
    organizationId: string,
    type: string,
    token: string,
    email: string,
    chargeable?: boolean
  ) {
    const values = this.cleanRequest({
      type,
      token,
      email,
      chargeable: chargeable ?? true
    });

    return new entities.OrganizationPaymentSource({
      organizationId,
      ...values
    }).save();
  }

  /**
   * Get payment source allowed for an organization.
   *
   * @return Promise
   */
  async getOrganizationPaymentSourcesAllowed(organizationId: string) {
    return entities.OrganizationPaymentSource.getAllowed(organizationId);
  }

  /**
   * Create a Setup Intent for an organization
   *
   * @return Promise: { client_secret, public_key }
   */
  async createOrganizationPaymentSourceIntent(organizationId: string) {
    return entities.OrganizationPaymentSource.intent(organizationId);
  }

  /**
   * Get the organization  profile.
   *
   * @param {string} organizationId - id of the organization.
   * @return Promise
   */
  async getOrganizationProfile(organizationId: string) {
    return entities.OrganizationProfile.get({ organizationId });
  }

  /**
   * Update a organization profile.
   *
   * @param {string} id - id of the organization.
   * @param {obj} data - fields to update on the profile
   *
   * @return Promise
   */
  async updateOrganizationProfile(id: string, data: APIObject) {
    const { api_url } = getConfig();
    const updatedProfile = await request(
      `${api_url}/organizations/${id}/profile`,
      "PATCH",
      data
    );

    return new entities.OrganizationProfile(updatedProfile);
  }

  /**
   * Get a item from the registry.
   *
   * @param {string} organizationId - id of the organization.
   *
   * @return Promise
   */
  async getSetupRegistry(organizationId: string) {
    return entities.SetupRegistry.get(organizationId);
  }

  /**
   * Get a item from the registry.
   *
   * @param {string} organizationId - id of the organization.
   * @param {object} settings - settings including the name of the registry
   * item.
   * @return Promise
   */
  async getSetupConfig(
    organizationId: string,
    settings: { service?: string; format?: string } = {}
  ) {
    return entities.SetupConfig.get(organizationId, settings);
  }

  /*
   * Get connected accounts for a user
   *
   * @param {string} id - id of the user.
   *
   * @return Promise ConnectedAccounts[]
   */
  async getConnectedAccounts(userId: string) {
    return entities.ConnectedAccount.query(userId);
  }

  /**
   * Get a list of tickets based on the settings.
   */
  async getTickets(settings: TicketQueryParams) {
    return entities.Ticket.query(settings);
  }

  /**
   * Update the status of a ticket.
   *
   * @param {string|number} ticketId Ticket to be updated
   * @param {string} status New status to be applied
   *
   * @return Promise<Ticket>
   */
  async updateTicketStatus(ticketId: string | number, status: string) {
    return entities.Ticket.patch(ticketId, { status }).then(ticket => ticket);
  }

  /**
   * Get a list of available priorities for the subscription ID.
   *
   * @param {string|number} subscription_id
   * @param {string} category
   *
   * @return Promise<Priority[]>
   */
  async getTicketPriorities(subscription_id?: string, category?: string) {
    const priorities = await entities.TicketPriority.get({
      subscription_id,
      category
    });
    return priorities.map(priority => new entities.TicketPriority(priority));
  }

  /**
   * Return a list of available categories for a ticket
   *
   * @return Promise<Category[]>
   */
  async getTicketCategories() {
    const categories = await entities.TicketCategory.get();
    return categories.map(priority => new entities.TicketCategory(priority));
  }

  /**
   * Get all the attachments related to a ticket, even the ones included in the comments
   *
   * @param {number|string} ticketId
   *
   * @return Promise<Attachment[]>
   */
  async getAllTicketAttachments(ticketId: string | number) {
    const response = await entities.Ticket.getAllAttachments(ticketId);
    return response.map(attachment => ({
      filename: attachment.filename,
      url: attachment.uri,
      contentType: attachment.content_type
    }));
  }

  /**
   * Open a ticket
   *
   * @param {object} ticket
   *
   * @return Promise<Ticket>
   */
  async openTicket(ticket: APIObject) {
    const response = await entities.Ticket.open(ticket);
    return response;
  }

  /**
   * Load comments for a ticket, excluding the initial comment.
   */
  async loadComments(ticketId: string, params: Record<string, any>) {
    const data = await entities.Comment.query(ticketId, params);
    const page = params.page || 1;
    const PAGE_SIZE = 50;
    const pages = Math.ceil(data.count / PAGE_SIZE);
    const isPreviousToLastPage = page === pages - 1;
    const isLastPage = page === pages;

    if (isPreviousToLastPage && data.count % PAGE_SIZE === 1) {
      delete data._links?.next;
    }

    data.count -= 1;

    if (isLastPage) {
      data.comments = data.comments.slice(0, -1);
    }

    return {
      ...data,
      comments: data.comments.map(comment => ({
        ...comment,
        attachments: Object.values(comment.attachments || {}).map(
          attachment => ({
            filename: attachment.file_name,
            url: attachment.uri,
            contentType: attachment.content_type
          })
        )
      }))
    };
  }

  /**
   * Send a new comment.
   *
   * @param {Object} comment
   *
   * @return Promise<Comment>
   */
  async sendComment(comment: APIObject) {
    return new entities.Comment(comment).save();
  }

  /**
   * Create an invitation
   * @deprecated Use createInvitationWithEnvironmentType() instead
   *
   * @param {string} email
   * @param {string} projectId
   * @param {string} role project role
   * @param {array} Environments Array of environment object id/role
   *
   * @returns {Promise} Promise that return a Result.
   */
  async createInvitation(
    email: string,
    projectId: string,
    role: string,
    environments: { id: string; role: string }[],
    force = false
  ) {
    const invitation = new entities.Invitation({
      email,
      projectId,
      environments,
      role,
      force
    });

    return invitation.save();
  }

  /**
   * Create an organization invitation
   *
   * @param {string} email
   * @param {string} organizationId
   * @param {array} permissions
   * @param {bool} force
   *
   * @returns {Promise} Promise that return a Result.
   */
  async createOrganizationInvitation(
    email: string,
    organizationId: string,
    permissions: string[],
    force = false
  ) {
    const invitation = new entities.OrganizationInvitation({
      email,
      organizationId,
      permissions,
      force
    });

    return invitation.save();
  }

  /**
   * Create an invitation with environment types
   *
   * @param {string} email
   * @param {string} projectId
   * @param {string} role project role
   * @param {array} Permissions Array of environment types object id/role
   *
   * @returns {Promise} Promise that return a Result.
   */
  async createInvitationWithEnvironmentTypes(
    email: string,
    projectId: string,
    role: string,
    permissions: {
      type: InvitationEnvironmentType;
      role: EnvironmentAccessRole;
    }[],
    force = false
  ) {
    const invitation = new entities.Invitation({
      email,
      projectId,
      permissions,
      role,
      force
    });

    return invitation.save();
  }

  /**
   * Get project invitations list
   *
   * @param {string} projectId
   * @param {string} id
   *
   * @returns {Promise} Promise that return an inivitations list.
   */
  async getInvitations(projectId: string) {
    return entities.Invitation.query(projectId);
  }

  /**
   * Get project environment types
   *
   * @param {string} projectId
   *
   * @returns {Promise} Promise that return an environment types list.
   */
  async getProjectEnvironmentTypes(projectId: string) {
    return entities.EnvironmentType.query({ projectId });
  }

  /**
   * Get project environment types accesses
   *
   * @param {string} projectId
   *
   * @returns {Promise} Promise that return an environment types accesses list.
   */
  async getProjectEnvironmentTypesWithAccesses(projectId: string) {
    const environmentTypes = await this.getProjectEnvironmentTypes(projectId);
    for (const environmentType of environmentTypes) {
      // eslint-disable-next-line no-await-in-loop
      await environmentType.getAccesses();
    }

    return environmentTypes;
  }

  async updateEnvironmentTypeAccess(params: UpdateAccessParams) {
    return entities.EnvironmentType.updateAccess(params);
  }

  async deleteEnvironmentTypeAccess(params: DeleteAccessParams) {
    return entities.EnvironmentType.deleteAccess(params);
  }

  async createEnvironmentTypeAccess(params: CreateAccessParams) {
    return entities.EnvironmentType.createAccess(params);
  }

  async getDunningActions(organizationId: string) {
    const { api_url } = getConfig();

    return request(
      `${api_url}/organizations/${organizationId}/dunning-actions`
    ) as Promise<{
      items: {
        date: string;
        days_until: number;
        id: string;
        order_id: string;
        type: "charge" | "suspend" | "revoke";
        uid: string;
      }[];
      _links: { self: { href: string } };
    }>;
  }

  async setVerificationMethodAsPaymentMethod(organizationId: string) {
    return entities.OrganizationPaymentSource.setVerificationMethodAsPaymentMethod(
      organizationId
    );
  }
}
